import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSeo } from "../hooks/useSeo";
import { buildCanonicalUrl } from "../seo/config";
import type { LayoutOutletContext } from "../layout/types";

type JoinLink = {
  id: string;
  label: string;
  href?: string;
  alt?: string;
};

export const JoinNow = () => {
  const { t } = useTranslation();
  const { locale } = useOutletContext<LayoutOutletContext>();
  const pageTitle = t("pages.joinNow.title");
  const siteTitle = t("site.title");
  const instructions = t("pages.joinNow.instructions");
  
  useSeo({
    title: `${pageTitle} | ${siteTitle}`,
    description: instructions,
    canonical: buildCanonicalUrl("join-now/", locale),
    locale,
  });

  const localizedLinks = t("pages.joinNow.links", {
    returnObjects: true,
  }) as JoinLink[];

  const downloadsBase = `${import.meta.env.BASE_URL}downloads/`;
  const assetBase = `${import.meta.env.BASE_URL}assets/join-now/`;

  type LinkId = "family" | "secondary" | "open";
  const downloadFilenames: Record<LinkId, string> =
    locale === "en"
      ? {
          family:
            "CPCE_Basic%20Law%20QA%202025-26_Family%20Primary_A4_V3_EN.jpg",
          secondary:
            "CPCE_Basic%20Law%20QA%202025-26_Secondary_A4_V2_EN.jpg",
          open:
            "CPCE_Basic%20Law%20QA%202025-26_Open_A4_V3_EN.jpg",
        }
      : {
          family:
            "CPCE_Basic%20Law%20QA%202025-26_Family%20Primary_A4_V3.jpg",
          secondary:
            "CPCE_Basic%20Law%20QA%202025-26_Secondary_A4_V2.jpg",
          open:
            "CPCE_Basic%20Law%20QA%202025-26_Open_A4_V3.jpg",
        };

  const imageFilenames: Record<LinkId, string> =
    locale === "en"
      ? {
          family: "join-family-en.png",
          secondary: "join-sec-en.png",
          open: "join-open-en.png",
        }
      : {
          family: "join-family.png",
          secondary: "join-sec.png",
          open: "join-open.png",
        };

  const joinLinks = localizedLinks.map((link) => {
    const id = link.id as LinkId;
    const image = `${assetBase}${imageFilenames[id] ?? imageFilenames.family}`;
    const href = `${downloadsBase}${downloadFilenames[id] ?? downloadFilenames.family}`;

    return {
      ...link,
      image,
      href,
      isExternal: false,
    };
  });

  return (
    <article className="page page--join">
      <p className="page__intro page__intro--join">{instructions}</p>
      <div className="join-now__links">
        {joinLinks.map(({ id, label, href, alt, image, isExternal }) => (
          <a
            key={id}
            href={href}
            className="join-now__link"
            aria-label={label}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
          >
            <img src={image} alt={alt || label} className="join-now__image" />
          </a>
        ))}
      </div>
    </article>
  );
};
